'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useAuth } from '@workspace/ui/hooks/useAuth';
import { refreshAuth } from '@workspace/network';
import {
  bounceTo,
  bouncedRecently,
  clearPendingReturnTo,
  readAccessExpiresAt,
  rememberReturnTo,
  useReturnTo,
} from '@/hooks/useReturnTo';

/**
 * Pages part-way through completing a sign-in or an account operation. They
 * consume a one-time code/token/state, or are linking an account to a session
 * that already exists, so redirecting out from under them would break the flow.
 * Each finishes by calling `redirectAfterAuth()` or landing on a page the guard
 * does cover, so the pending `return_to` is still honoured afterwards.
 */
const MID_FLOW_PATHS = [
  '/login/verify',
  '/login/magic-link/verify',
  '/login/atproto/callback',
  '/register/magic-link/verify',
  '/mfa/challenge',
  '/verify-email',
  '/password/reset',
  '/connect/oauth',
];

function isMidFlow(pathname: string): boolean {
  return MID_FLOW_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Send a signed-in visitor on to their `return_to`, from any page.
 *
 * Mounted once in the root layout. A service sends someone here to
 * authenticate; whether they turn out to be signed in already, have a session
 * that only needs refreshing, or sign in through a flow that drops the param on
 * the way to /profile, the outcome should be the same: back to the service,
 * hands-free. Before this, the redirect lived only on the login entry pages and
 * the completion handlers, so any route that lost the param stranded them on
 * /profile.
 *
 * Gated on a `return_to` deliberately — it is the only unambiguous signal of
 * intent. Without one, pages render normally (with a signed-in notice on
 * /login) rather than guessing.
 */
export function ReturnToGuard() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { authenticated, loading } = useAuth();
  const { returnTo } = useReturnTo();
  const attempting = useRef(false);

  // Remember the param on arrival so later pages in the visit can honour it.
  // Not on mid-flow pages: those carry their own param to completion, and a
  // delegation flow (/connect/oauth) is not a sign-in, so its target must not
  // linger to steer a later visit.
  const rawReturnTo = searchParams.get('return_to');
  useEffect(() => {
    if (!isMidFlow(pathname)) rememberReturnTo(rawReturnTo);
  }, [rawReturnTo, pathname]);

  useEffect(() => {
    if (loading || !authenticated || !returnTo || attempting.current) return;
    if (isMidFlow(pathname)) return;

    // Already where it points (a relative `return_to` of the current page).
    const target = new URL(returnTo, window.location.href);
    if (target.href === window.location.href) {
      clearPendingReturnTo();
      return;
    }

    if (bouncedRecently(target.href)) {
      console.warn(
        `[auth] not redirecting to ${target.href} again — it sent the visitor straight back, so it is not accepting this session`,
      );
      clearPendingReturnTo();
      return;
    }

    attempting.current = true;
    // Mint a fresh access token first when the current one has expired.
    //
    // `authenticated` tracks the *refresh* token, so we reach here with a dead
    // access token — precisely the case that sent the user to an auth page to
    // begin with. useAuth's proactive scheduler does fire immediately for an
    // expired token, but asynchronously: redirecting straight away races it and
    // can hand the calling service the expired token, which bounces the user
    // back here. Awaiting closes that loop. (Refresh tokens are stateless JWTs,
    // so racing the scheduler's own refresh is wasteful but harmless.)
    //
    // A failed refresh is not fatal — the refresh token may have been revoked
    // server-side — so leave them on the page to sign in, which comes back here
    // with a fresh session and the pending `return_to` still remembered.
    const go = async () => {
      const accessExpiresAt = readAccessExpiresAt();
      if (accessExpiresAt === null || accessExpiresAt <= Date.now()) {
        if (!(await refreshAuth())) {
          attempting.current = false;
          return;
        }
      }
      bounceTo(target.href);
    };
    void go();
  }, [loading, authenticated, returnTo, pathname]);

  return null;
}
